import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollRevealManager
 *
 * Uses IntersectionObserver to apply the `.is-visible` class to any element
 * with a `data-animate` attribute when it enters the viewport.
 *
 * - Animations are transform/opacity only (GPU-safe)
 * - Uses the "luxury ease" cubic-bezier from the design system via CSS
 * - Respects prefers-reduced-motion at the JS level (bails early)
 */
function ScrollRevealManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) return;

    const candidates = Array.from(document.querySelectorAll('[data-animate]'));
    if (!candidates.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        root: null,
        // Slight bottom margin so elements are fully in view before animating
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.15,
      }
    );

    candidates.forEach((el) => {
      if (!el.classList.contains('is-visible')) {
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, [pathname]);

  return null;
}

export default ScrollRevealManager;

